/**
 * Multipart upload with real progress reporting.
 *
 * `fetch()` cannot report *upload* progress - there is no readable stream for the
 * request body in any shipping browser - so anything that needs a progress bar
 * has to go through XMLHttpRequest. This is the one place in the app that does,
 * and it mirrors `fetchWithAuth`'s behaviour otherwise: fresh Clerk token per
 * request, the `x-session-id` header, and a single retry on 401 with a
 * cache-skipping token.
 */

import * as Sentry from "@sentry/react";

export interface UploadProgress {
  /** Bytes handed to the socket so far. */
  loaded: number;
  /** Total bytes of the request body, or 0 when the browser cannot tell. */
  total: number;
  /** 0-1. Stays at 0 while total is unknown. */
  fraction: number;
}

export interface UploadWithProgressOptions {
  url: string;
  body: FormData;
  session: any;
  method?: "POST" | "PUT" | "PATCH";
  onProgress?: (progress: UploadProgress) => void;
  /**
   * Fired once the last byte has left the browser. Everything after this point is
   * the server doing its work (SharePoint write, database, side effects), which
   * is what the queue uses to estimate per-file server time.
   */
  onUploadComplete?: () => void;
  signal?: AbortSignal;
  /** Guards against retrying forever; set internally. */
  isRetry?: boolean;
}

/** Thrown for any non-2xx response so callers can branch on status. */
export class UploadHttpError extends Error {
  readonly status: number;
  readonly responseText: string;

  constructor(status: number, responseText: string, url: string) {
    super(
      `Upload naar ${url} mislukt met status ${status}${
        responseText ? `: ${responseText.slice(0, 500)}` : ""
      }`,
    );
    this.name = "UploadHttpError";
    this.status = status;
    this.responseText = responseText;
  }

  /**
   * 5xx and 429 are worth retrying; a 4xx means the payload itself is wrong and
   * retrying would just repeat the same failure.
   */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

/** Thrown when the request was aborted by the caller. */
export class UploadAbortedError extends Error {
  constructor(url: string) {
    super(`Upload naar ${url} is geannuleerd`);
    this.name = "UploadAbortedError";
  }
}

/** Thrown when the request never reached the server (offline, DNS, CORS). */
export class UploadNetworkError extends Error {
  readonly isRetryable = true;

  constructor(url: string) {
    super(`Geen verbinding met de server tijdens upload naar ${url}`);
    this.name = "UploadNetworkError";
  }
}

const parseResponse = (xhr: XMLHttpRequest): any => {
  const text = xhr.responseText;
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // The backend occasionally answers with a bare string; that is not an error.
    return text;
  }
};

const sendXhr = (
  options: UploadWithProgressOptions,
  token: string,
): Promise<{ status: number; data: any }> =>
  new Promise((resolve, reject) => {
    const { url, body, method = "POST", onProgress, onUploadComplete, signal } =
      options;

    if (signal?.aborted) {
      reject(new UploadAbortedError(url));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.withCredentials = true;
    xhr.responseType = "text";

    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    if (options.session?.id) {
      xhr.setRequestHeader("x-session-id", options.session.id);
    }
    // Content-Type is deliberately not set: the browser must add the multipart
    // boundary itself.

    const onAbort = () => xhr.abort();
    signal?.addEventListener("abort", onAbort, { once: true });

    const cleanup = () => signal?.removeEventListener("abort", onAbort);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        onProgress({
          loaded: event.loaded,
          total: event.lengthComputable ? event.total : 0,
          fraction: event.lengthComputable && event.total > 0
            ? event.loaded / event.total
            : 0,
        });
      };
    }

    xhr.upload.onload = () => onUploadComplete?.();

    xhr.onload = () => {
      cleanup();
      const data = parseResponse(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status, data });
        return;
      }
      reject(new UploadHttpError(xhr.status, xhr.responseText || "", url));
    };

    xhr.onerror = () => {
      cleanup();
      reject(new UploadNetworkError(url));
    };

    xhr.ontimeout = () => {
      cleanup();
      reject(new UploadNetworkError(url));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new UploadAbortedError(url));
    };

    xhr.send(body);
  });

/**
 * POSTs a FormData body with progress callbacks. Resolves with the parsed
 * response, rejects with one of the typed errors above.
 */
export const uploadWithProgress = async (
  options: UploadWithProgressOptions,
): Promise<any> => {
  const { url, session, isRetry = false } = options;

  if (!session || typeof session.getToken !== "function") {
    const error = new Error(
      "Sessie is ongeldig of getToken ontbreekt; upload kan niet worden gestart.",
    );
    Sentry.captureException(error, {
      extra: { context: "uploadWithProgress.session", url },
    });
    throw error;
  }

  const token = await session.getToken();

  try {
    const { data } = await sendXhr(options, token);
    return data;
  } catch (error) {
    // A 401 usually just means the token aged out mid-batch. Refresh once and
    // retry; a second 401 means the session is genuinely gone.
    if (
      error instanceof UploadHttpError &&
      error.status === 401 &&
      !isRetry
    ) {
      const freshToken = await session.getToken({ skipCache: true });
      const { data } = await sendXhr({ ...options, isRetry: true }, freshToken);
      return data;
    }

    // Aborts are user intent, not incidents - keep them out of Sentry.
    if (!(error instanceof UploadAbortedError)) {
      Sentry.captureException(error, {
        extra: {
          context: "uploadWithProgress",
          url,
          status:
            error instanceof UploadHttpError ? error.status : undefined,
          responseText:
            error instanceof UploadHttpError
              ? error.responseText?.slice(0, 1000)
              : undefined,
        },
      });
    }

    throw error;
  }
};
